import React, { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import PanelAlert from '../../../shared/components/PanelAlert';

/**
 * FMO Panel Alert Component
 * Specialized PanelAlert that analyzes selected files for FMO processing
 */
const FMOPanelAlert = ({ selectedRows, availableClusters }) => {
  const theme = useTheme();
  // Analysis of selected files
  const fileAnalysis = useMemo(() => {
    if (selectedRows.length === 0) {
      return { targets: [], isSingleTarget: false, filesByTarget: {}, missingTargetCount: 0 };
    }
    
    const filesByTarget = {};
    let missingTargetCount = 0;
    
    selectedRows.forEach(row => {
      const target = row.target || '<none>';
      if (!row.target || row.target === '<none>' || row.target === '') {
        missingTargetCount++;
      }
      if (!filesByTarget[target]) {
        filesByTarget[target] = [];
      }
      filesByTarget[target].push(row);
    });
    
    const targets = Object.keys(filesByTarget);
    return {
      targets,
      isSingleTarget: targets.length === 1,
      filesByTarget,
      missingTargetCount
    };
  }, [selectedRows]);

  // Determine alert severity
  const getSeverity = () => {
    if (selectedRows.length === 0) return "warning";
    if (fileAnalysis.missingTargetCount > 0) return "error";
    return "info";
  };

  // Get primary message
  const getPrimaryMessage = () => {
    if (selectedRows.length === 0) {
      return 'Select structures to generate FMO input files';
    }
    if (fileAnalysis.missingTargetCount > 0) {
      return `${selectedRows.length} structure${selectedRows.length !== 1 ? 's' : ''} selected (${fileAnalysis.missingTargetCount} missing target)`;
    }
    return `${selectedRows.length} structure${selectedRows.length !== 1 ? 's' : ''} selected`;
  };

  // Get secondary message
  const getSecondaryMessage = () => {
    if (selectedRows.length === 0) {
      return 'FMO input files will be generated for selected structures';
    }
    if (fileAnalysis.missingTargetCount > 0) {
      return 'Target information is required for selected files';
    }
    if (!fileAnalysis.isSingleTarget) {
      return `Multiple targets detected (${fileAnalysis.targets.join(', ')}) - will create separate clusters`;
    }
    return `Target: ${fileAnalysis.targets[0]} • ${availableClusters.length} existing cluster${availableClusters.length !== 1 ? 's' : ''} available`;
  };

  return (
    <PanelAlert 
      severity={getSeverity()}
      primaryMessage={getPrimaryMessage()}
      secondaryMessage={getSecondaryMessage()}
      sx={{ 
        mx: theme.spacing(2), 
        mt: theme.spacing(2), 
        mb: theme.spacing(1.5) 
      }}
    />
  );
};

// Export fileAnalysis calculation as a utility for parent component to use
export const calculateFileAnalysis = (selectedRows) => {
  if (selectedRows.length === 0) {
    return { targets: [], isSingleTarget: false, filesByTarget: {}, missingTargetCount: 0 };
  }
  
  const filesByTarget = {};
  let missingTargetCount = 0;
  
  selectedRows.forEach(row => {
    const target = row.target || '<none>';
    if (!row.target || row.target === '<none>' || row.target === '') {
      missingTargetCount++;
    }
    if (!filesByTarget[target]) {
      filesByTarget[target] = [];
    }
    filesByTarget[target].push(row);
  });
  
  const targets = Object.keys(filesByTarget);
  return {
    targets,
    isSingleTarget: targets.length === 1,
    filesByTarget,
    missingTargetCount
  };
};

export default FMOPanelAlert;